import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/mutations/createSimpleMutation');

/**
 * Simple mutation factory for straightforward CRUD operations.
 * Use this for sub-resources or simple operations that don't need
 * the full power of createResourceMutations.
 * @module hooks/mutations/createSimpleMutation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
/**
 * Configuration for creating a simple mutation hook
 */
interface SimpleMutationConfig<TData, TVariables> {
  /** Function that performs the API call */
  mutationFn: (variables: TVariables) => Promise<TData>;
/** Query keys to invalidate on success */
  invalidateKeys: readonly (readonly unknown[])[];
  /** Toast message shown on success */
  successMessage: string;
  /** Toast message prefix shown on error */
  errorMessage: string;
  /** Optional callback executed on success */
  onSuccess?: (data: TData, variables: TVariables) => void;
}

/**
 * Configuration for creating sub-resource CRUD mutations
 */
interface SubResourceMutationsConfig<TData, TCreate, TUpdate> {
  /** Display name for the resource (e.g., "Category", "Location") */
  resourceName: string;
  /** API methods for CRUD operations */
  api: {
    create: (data: TCreate) => Promise<TData>;
    update: (id: string, data: TUpdate) => Promise<TData>;
    delete: (id: string) => Promise<void>;
  };
  /** Primary query key to invalidate */
  primaryKey: () => readonly unknown[];
  /** Additional keys to invalidate on mutations */
  additionalKeys?: () => readonly (readonly unknown[])[];
}

/**
 * Creates a simple mutation hook with standardized toast messages and query invalidation.
 *
 * @example
 * ```typescript
 * const useArchiveProject = createSimpleMutation({
 *   mutationFn: (id: string) => projectApi.archive(id),
 *   invalidateKeys: [queryKeys.projects.lists()],
 *   successMessage: 'Project archived successfully',
 *   errorMessage: 'Failed to archive project',
 * });
 * ```
 */
export function createSimpleMutation<TData, TVariables>(
  config: SimpleMutationConfig<TData, TVariables>
) {
  return function useSimpleMutation() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: config.mutationFn,
      onSuccess: (data, variables) => {
        config.invalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key as unknown[] });
        });
        config.onSuccess?.(data, variables);
        toast.success(config.successMessage);
      },
      onError: (error) => {
        logger.error(`[${config.errorMessage}]`, error);
        toast.error(error instanceof Error ? error.message : config.errorMessage);
      },
    });
  };
}

/**
 * Creates CRUD mutation hooks for a sub-resource.
 * Returns pre-configured hooks for create, update, and delete operations.
 *
 * @example
 * ```typescript
 * const categoryMutations = createSubResourceMutations({
 *   resourceName: 'Category',
 *   api: {
 *     create: inventoryApi.createCategory,
 *     update: inventoryApi.updateCategory,
 *     delete: inventoryApi.deleteCategory,
 *   },
 *   primaryKey: () => queryKeys.inventory.categories(),
 *   additionalKeys: () => [queryKeys.inventory.lists()],
 * });
 *
 * export const useCreateCategoryMutation = categoryMutations.useCreateMutation;
 * export const useUpdateCategoryMutation = categoryMutations.useUpdateMutation;
 * export const useDeleteCategoryMutation = categoryMutations.useDeleteMutation;
 * ```
 */
export function createSubResourceMutations<TData, TCreate, TUpdate>(
  config: SubResourceMutationsConfig<TData, TCreate, TUpdate>
) {
  const getInvalidateKeys = (): readonly (readonly unknown[])[] => {
    const keys: (readonly unknown[])[] = [config.primaryKey()];
    if (config.additionalKeys) {
      keys.push(...config.additionalKeys());
    }
    return keys;
  };

  const useCreateMutation = createSimpleMutation<TData, { data: TCreate }>({
    mutationFn: ({ data }) => config.api.create(data),
    invalidateKeys: getInvalidateKeys(),
    successMessage: `${config.resourceName} created successfully`,
    errorMessage: `Failed to create ${config.resourceName.toLowerCase()}`,
  });

  const useUpdateMutation = createSimpleMutation<TData, { id: string; data: TUpdate }>({
    mutationFn: ({ id, data }) => config.api.update(id, data),
    invalidateKeys: getInvalidateKeys(),
    successMessage: `${config.resourceName} updated successfully`,
    errorMessage: `Failed to update ${config.resourceName.toLowerCase()}`,
  });

  const useDeleteMutation = createSimpleMutation<void, string>({
    mutationFn: config.api.delete,
    invalidateKeys: getInvalidateKeys(),
    successMessage: `${config.resourceName} deleted successfully`,
    errorMessage: `Failed to delete ${config.resourceName.toLowerCase()}`,
  });

  return {
    useCreateMutation,
    useUpdateMutation,
    useDeleteMutation,
  };
}
